<template>
    <div class="imageview__wrapper">
        <link
            rel="stylesheet"
            :href="'/css/' + $route.params.treeslug + '/styles'"
        />
        <div
            class="imageview"
            :style="'background-color: ' + mergedTree.background_color + ';'"
        >
            <div
                class="imageview__header"
                :style="'background-image:url(' + backgroundImage + ');'"
            >
                <div
                    v-if="profileImage"
                    class="imageview__logo"
                    :style="'background-image:url(' + profileImage + ');'"
                ></div>

                <div class="imageview__header__links">
                    <h2>{{ mergedTree.newsletter_info_text }}</h2>

                    <a
                        :href="createLink(link.url, link.slug)"
                        class="imageview__header__links__item"
                        v-for="(link, index) in highlightLinks"
                        :key="index"
                        target="_blank"
                    >
                        {{ link.text }}
                    </a>
                </div>
            </div>
            <div
                v-if="
                    mergedTree.newsletter_button_label &&
                        mergedTree.newsletter_link
                "
                class="imageview__newsletter"
                :style="
                    'background-color: ' + mergedTree.background_color + ';'
                "
            >
                <a :href="mergedTree.newsletter_link">
                    {{ mergedTree.newsletter_button_label }}
                </a>
                <span>{{ mergedTree.newsletter_info_text }}</span>
            </div>
            <div
                v-if="mergedTree.hasOwnProperty('links')"
                class="imageview__links"
            >
                <div
                    v-for="l in filteredLinks"
                    :key="'key_' + l.slug"
                    class="imageview__links__wrapper"
                    :class="{
                        'imageview__links__wrapper--highlight': l.highlight,
                    }"
                >
                    <a
                        :href="createLink(l.url, l.slug)"
                        class="imageview__links__item"
                        :class="{
                            'imageview__links__item--highlight': l.highlight,
                        }"
                        :style="
                            'background-image:url(' +
                                l.gallery_image.data.thumbnails.big +
                                ');'
                        "
                    >
                        <span>{{ l.text }}</span>
                    </a>
                </div>
            </div>

            <div
                class="imageview__sociallinks"
                v-if="socialLinks && socialLinks.length > 0"
            >
                <div class="imageview__sociallinks__wrapper">
                    <a
                        :href="createLink(link.url, link.slug)"
                        class="imageview__sociallinks__item"
                        v-for="(link, index) in socialLinks"
                        :key="index"
                        target="_blank"
                    >
                        <Icon v-if="link.type.indexOf('social-custom') < 0">
                            <IconSocial :type="link.type.split('-')[1]" />
                        </Icon>
                        <span
                            v-else
                            class="imageview__sociallinks__item__custom"
                            v-html="link.image"
                        >
                        </span>
                    </a>
                </div>
            </div>

            <div
                class="imageview__footerlinks"
                v-if="footerLinks && footerLinks.length > 0"
            >
                <div class="imageview__footerlinks__wrapper">
                    <a
                        :href="createLink(link.url, link.slug)"
                        class="imageview__footerlinks__item"
                        v-for="(link, index) in footerLinks"
                        :key="index"
                        target="_blank"
                    >
                        {{ link.text }}
                    </a>
                </div>
            </div>

            <div class="imageview__imprint" v-html="activeTree.imprint"></div>
        </div>
    </div>
</template>

<script>
function compare(a, b) {
    if (a.position < b.position) return -1;
    if (a.position > b.position) return 1;
    return 0;
}

import { mapGetters } from 'vuex';
import Icon from '@/components/Icon.vue';
import IconSocial from '@/components/icons/IconSocial.vue';
import { useFavicon, useTitle } from '@vueuse/core';

export default {
    components: {
        Icon,
        IconSocial,
    },
    created() {
        if (this.public) {
            this.fetchPublicData(this.$route.params.treeslug);
        }

        if (this.public) {
            window.addEventListener('resize', () => {
                this.windowWidth = window.innerWidth;
            });
        }
    },
    props: {
        public: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapGetters(['activeTree', 'manipulatedData']),
        mergedTree() {
            if (this.public) {
                return this.activeTree;
            } else {
                return { ...this.activeTree, ...this.manipulatedData };
            }
        },
        backgroundImage() {
            if (
                this.windowWidth > 480 &&
                this.mergedTree.desktop_background_image &&
                this.mergedTree.desktop_background_image.data &&
                this.public
            ) {
                return this.mergedTree.desktop_background_image.data.thumbnails
                    .big;
            }
            if (
                this.mergedTree.background_image &&
                this.mergedTree.background_image.url
            ) {
                return this.mergedTree.background_image.url;
            }
            if (
                this.mergedTree.background_image &&
                this.mergedTree.background_image.data
            ) {
                return this.mergedTree.background_image.data.original;
            }
            return false;
        },
        profileImage() {
            if (this.mergedTree.image && this.mergedTree.image.url) {
                return this.mergedTree.image.url;
            }
            if (this.mergedTree.image && this.mergedTree.image.data) {
                return this.mergedTree.image.data.thumbnails.large;
            }
            return false;
        },
        filteredLinks() {
            if (
                Object.prototype.hasOwnProperty.call(this.mergedTree, 'links')
            ) {
                var links = this.mergedTree.links.data.filter(
                    l =>
                        Object.prototype.hasOwnProperty.call(
                            l,
                            'gallery_image',
                        ) && l.active,
                );

                links.sort(compare);

                if (this.mergedTree.reverse_link_order === 1) {
                    links = links.reverse();
                }

                return links;
            } else {
                return [];
            }
        },
        socialLinks() {
            if (this.mergedTree.links && this.mergedTree.links.data) {
                const links = this.mergedTree.links.data.filter(l => {
                    return l.active && l.type.includes('social-');
                });
                return links.sort((a, b) => {
                    return a.position - b.position;
                });
            } else {
                return false;
            }
        },
        footerLinks() {
            if (this.mergedTree.links && this.mergedTree.links.data) {
                const links = this.mergedTree.links.data.filter(l => {
                    return l.active && l.type.includes('footer');
                });
                return links.sort((a, b) => {
                    return a.position - b.position;
                });
            } else {
                return false;
            }
        },
        highlightLinks() {
            if (this.mergedTree.links && this.mergedTree.links.data) {
                const links = this.mergedTree.links.data.filter(l => {
                    return l.active && l.highlight;
                });
                return links.sort((a, b) => {
                    return a.position - b.position;
                });
            } else {
                return false;
            }
        },
    },
    data() {
        return {
            logoImage: '',
            newsletterBackgroundColor: '',
            fullyLoaded: false,
            windowWidth: window.innerWidth,
        };
    },
    methods: {
        async fetchPublicData(slug) {
            const action = this.public ? 'loadPublicTree' : 'loadTree';
            await this.$store.dispatch(action, {
                slug: slug,
            });

            const icon = useFavicon();
            const title = useTitle();

            // set favicon
            if (
                Object.prototype.hasOwnProperty.call(
                    this.activeTree,
                    'favicon_image',
                )
            ) {
                icon.value = this.activeTree.favicon_image.data.thumbnails.medium;
            } else if (
                Object.prototype.hasOwnProperty.call(
                    this.activeTree,
                    'image',
                ) ||
                Object.prototype.hasOwnProperty.call(
                    this.activeTree,
                    'background_image',
                )
            ) {
                icon.value = Object.prototype.hasOwnProperty.call(
                    this.activeTree,
                    'image',
                )
                    ? this.activeTree.image.data.thumbnails.medium
                    : this.activeTree.background_image.data.thumbnails.medium;
            }

            // set title
            var decodedString = document.createElement('div');
            decodedString.innerHTML = this.activeTree.name;
            title.value = decodedString.innerText + ' - clickthe.bio';
        },
        createLink(url, slug) {
            if (this.public) {
                return this.activeTree.slug + '/goto/' + slug;
            } else {
                return url;
            }
        },
    },
};
</script>

<style lang="scss">
.imageview {
    width: 100%;
    max-width: 650px;
    margin: 0 auto;
    min-height: 100%;

    &__wrapper {
        width: 100%;
    }

    &__header {
        height: 0;
        width: 100%;
        padding-bottom: 30%;
        background-position: center center;
        background-size: cover;
        position: relative;
        display: block;

        &__links {
            display: none;
        }
    }
    &__logo {
        position: absolute;
        z-index: 10;
        width: 100px;
        height: 100px;
        left: calc(50% - 50px);
        bottom: -10px;
        background: white;
        border-radius: 50%;
        background-position: center center;
        background-size: cover;

        @media screen and (max-width: 690px) {
            left: calc(50% - 10vw);
            width: 20vw;
            height: 20vw;
        }
    }
    &__newsletter {
        text-align: center;
        display: block;
        color: black;
        padding: 35px;
        letter-spacing: initial;

        @media screen and (max-width: 690px) {
            padding: 35px 15px;
        }

        a {
            background: black;
            display: inline-block;
            color: white;
            padding: 2px 13px 3px;
            border-radius: 15px;
            margin-bottom: 10px;

            @media screen and (max-width: 690px) {
                font-size: 15px;
            }
        }
        span {
            display: block;
            font-weight: normal;
            font-size: 12px;
        }
    }
    &__links {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;

        &__wrapper {
            position: relative;
            width: calc((100% / 3) - 4px);
        }
        &__item {
            display: block;
            width: 100%;
            height: 0;
            padding-bottom: 100%;
            background-size: cover;
            background-position: center center;

            span {
                display: none;
            }
        }
    }
    &__sociallinks {
        padding: 15px 10px;
        width: 100%;

        &__wrapper {
            display: flex;
            gap: 15px;
            justify-content: flex-end;
            align-items: center;
        }
        &__item {
            color: white;

            svg {
                width: 22px;
            }
        }
    }
    &__footerlinks {
        padding-top: 30px;
        padding-bottom: 40px;

        &__wrapper {
            display: flex;
            gap: 20px;
            justify-content: center;
        }
        &__item {
            font-size: 13px;
            text-decoration: underline;
            letter-spacing: initial;
        }
    }
    &__imprint {
        padding: 50px 0;
        letter-spacing: initial;
        font-weight: normal;
        font-size: 13px;

        @media screen and (max-width: 690px) {
            padding: 50px 15px;
        }

        ul,
        p {
            margin-bottom: 20px;

            &:last-child {
                margin-bottom: 0;
            }
        }
        ul {
            list-style-type: disc;
            margin-left: 15px;
        }
    }
}
</style>
